import { BadgeCompany, COMPANY_ANNIVERSARY_PALETTE } from './company.js'
import { tierOf } from './tiers.js'
import { BADGE_PALETTE_KEYS, BADGE_SHAPE_KEYS, BadgePaletteKey, BadgeShapeKey } from './types.js'

export const isBadgeShapeKey = (v: string): v is BadgeShapeKey =>
    (BADGE_SHAPE_KEYS as readonly string[]).includes(v)
export const isBadgePaletteKey = (v: string): v is BadgePaletteKey =>
    (BADGE_PALETTE_KEYS as readonly string[]).includes(v)

export interface BadgeArtInput {
    score: number
    isSpecial: boolean
    isAnniversary: boolean
    anniversaryYears: number | null
    /** Resolved icon markup (64-grid); ignored for anniversary badges, which draw the year. */
    iconMarkup: string | null
    /** The badge's categories; the first by `name_en` decides shape and enamel. */
    categories: { name_en: string; shape: string; palette: string }[]
    /** Owner's company: anniversary badges take its colours (pink without one). */
    company?: BadgeCompany | null
}

export interface BadgeArtSpecResult {
    shape: BadgeShapeKey
    palette: BadgePaletteKey
    tier: ReturnType<typeof tierOf>
    iconMarkup: string | null
    anniversaryYears: number | null
}

/**
 * The one place that turns badge data into a drawable spec, shared by the server (API, PNG)
 * and the admin preview so the form shows exactly what the Hub will. The two admin flags
 * drive the look directly and need no category: an anniversary badge is always a ring medal
 * in the company colour (or pink), and `tierOf` makes a special badge crystal. Everything
 * else takes shape and enamel from the first category, falling back to a diamond when the
 * badge has no category yet.
 */
export const resolveBadgeArt = (input: BadgeArtInput): BadgeArtSpecResult => {
    const first = [...input.categories].sort((a, b) => a.name_en.localeCompare(b.name_en))[0]
    // A years value without the flag still counts, a flag without years draws year 1 (the
    // schema rejects that combination for new/edited badges).
    const isAnniversary = input.isAnniversary || input.anniversaryYears !== null
    const anniversaryYears = isAnniversary ? (input.anniversaryYears ?? 1) : null
    const shape: BadgeShapeKey = isAnniversary
        ? 'ringmedal'
        : first && isBadgeShapeKey(first.shape)
          ? first.shape
          : 'diamond'
    const categoryPalette: BadgePaletteKey =
        first && isBadgePaletteKey(first.palette) ? first.palette : 'yellow'
    const palette: BadgePaletteKey = isAnniversary
        ? input.company
            ? COMPANY_ANNIVERSARY_PALETTE[input.company]
            : 'pink'
        : categoryPalette
    return {
        shape,
        palette,
        tier: tierOf({ score: input.score, anniversaryYears, isSpecial: input.isSpecial }),
        iconMarkup: isAnniversary ? null : input.iconMarkup,
        anniversaryYears,
    }
}
